// NEVANU — App Shell
// Dashboard-Gerüst, Bottom Navigation, More Sheet, Profil Sheet,
// Header FAB, Login/Auth, Session, DOMContentLoaded Init
//
// Dieses Modul ersetzt die Shell-Infrastruktur aus form.js und
// verdrahtet loadDashboard() mit dem TS-Router (navigateTo).

import { api } from '../core/api-client';
import { getSession, saveSession, clearSession, isManager, getRoleLabel } from '../core/auth';
import { escHtml, escAttr, showToast, addBottomSheetSwipe } from '../core/dom';
import { navigateTo } from '../core/router';
import { cycleThemeMode, initTheme } from '../core/theme';

// ============================================================
// TYPES
// ============================================================

interface NavTab {
  id: string;
  icon: string;
  label: string;
  gradFrom?: string;
  gradTo?: string;
}

interface FabItem {
  icon: string;
  label: string;
  action: string;
}

// ============================================================
// STATE
// ============================================================

let bottomSheetOpen = false;
let headerFabOpen = false;

/** Aktiver Dashboard-Tab (persistiert in sessionStorage) */
let activeDashTab: string = (() => {
  try { return sessionStorage.getItem('nevanu_dashTab') || 'home'; } catch { return 'home'; }
})();

// ============================================================
// CONSTANTS
// ============================================================

/** @deprecated — Use window.NAV_ITEMS from nav-registry.js instead */
const BOTTOM_NAV_TABS: NavTab[] = [
  { id: 'home', icon: 'home', label: 'Home' },
  { id: 'search', icon: 'search', label: 'Suche' },
  { id: 'add', icon: 'add', label: '' },
  { id: 'notifications', icon: 'notifications', label: 'Updates' },
  { id: 'profil', icon: 'person', label: 'Profil' },
];

/** @deprecated — Use window.NAV_ITEMS/NAV_SECTIONS from nav-registry.js instead */
const SALON_NAV_ITEMS: NavTab[] = [
  { id: 'home', icon: 'home', label: 'Übersicht' },
  { id: 'kalender', icon: 'calendar_month', label: 'Kalender' },
  { id: 'neukunden', icon: 'assignment', label: 'Buchungen' },
  { id: 'terminliste', icon: 'list_alt', label: 'Termineingänge' },
  { id: 'kasse', icon: 'point_of_sale', label: 'Kasse' },
  { id: 'zeiterfassung', icon: 'avg_pace', label: 'Stempeln' },
  { id: 'aufgaben', icon: 'check_circle', label: 'Aufgaben' },
  { id: 'rechner', icon: 'calculate', label: 'Preisrechner' },
];

/** @deprecated — Use window.NAV_ITEMS from nav-registry.js instead */
const TEAM_NAV_ITEMS: NavTab[] = [
  { id: 'telefon', icon: 'call', label: 'Telefon' },
  { id: 'chat', icon: 'chat', label: 'Chat' },
  { id: 'musik', icon: 'music_note', label: 'Musik' },
  { id: 'news', icon: 'newspaper', label: 'News' },
  { id: 'kollegen', icon: 'people', label: 'Kollegen' },
];

/** @deprecated — Use window.NAV_ITEMS from nav-registry.js instead */
const TOOLS_NAV_ITEMS: NavTab[] = [
  { id: 'kunden', icon: 'person', label: 'Kunden' },
  { id: 'lager', icon: 'inventory_2', label: 'Lager' },
  { id: 'bestellungen', icon: 'local_shipping', label: 'Bestellungen' },
  { id: 'protokolle', icon: 'description', label: 'Protokolle' },
  { id: 'qualityscore', icon: 'star_rate', label: 'QualityScore' },
  { id: 'reviews', icon: 'reviews', label: 'Reviews' },
  { id: 'reglement', icon: 'menu_book', label: 'Reglement' },
  { id: 'mediahub_galerie', icon: 'photo_library', label: 'MediaHub' },
  { id: 'dokumente', icon: 'folder_open', label: 'Dokumente Hub' },
  { id: 'learning', icon: 'school', label: 'Lern-Hub' },
  { id: 'geraete', icon: 'devices', label: 'Geräte' },
  { id: 'neva', icon: 'auto_awesome', label: 'Ask Neva' },
  { id: 'berichte', icon: 'bar_chart', label: 'Berichte' },
  { id: 'invoicehub', icon: 'receipt_long', label: 'Finance Hub' },
  { id: 'team', icon: 'group', label: 'HR Hub' },
  { id: 'leistungen', icon: 'content_cut', label: 'Leistungen' },
];

// Legacy: combined list for backward compatibility
const MORE_NAV_ITEMS: NavTab[] = [
  ...SALON_NAV_ITEMS,
  ...TEAM_NAV_ITEMS,
  ...TOOLS_NAV_ITEMS,
];

const IN_MORE_TABS = [
  'bestellungen', 'protokolle', 'news', 'kollegen', 'team', 'export',
  'profil', 'lager', 'kunden', 'leistungen', 'arbeitszeiten', 'projekt',
  'mediahub_kamera', 'mediahub_galerie', 'neva', 'berichte', 'invoicehub',
  'learning', 'qualityscore', 'reviews', 'reglement', 'dokumente', 'kasse', 'geraete',
  'kampagnen', 'terminliste', 'telefon', 'passwoerter',
];

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 min

/** NEVANU SVG Logo (identisch mit form.js NEVANU_SVG_SMALL) */
const NEVANU_SVG_LOGO = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.64 273.62" style="height:24px;width:auto;vertical-align:middle"><g><path fill="#F27182" d="M158.8,17.09h-57.26v.76c6.46,10.21,12.05,20.42,16.79,30.63,15.5,33.28,21.09,68.06,23.68,122.51L65.81,17.09H14.58v.76c12.05,16.26,17.22,28.36,17.22,57.1,0,33.28-2.58,105.88-6.89,156.54l-2.58,31.01v.38h57.26v-.38c-4.74-10.21-9.9-21.93-13.35-30.63-23.25-58.61-31-110.03-31-181.12l104.61,212.13h11.62c-.43-7.56-5.6-94.91-7.32-132.34-1.29-25.33.43-52.94,6.03-79.78,2.15-10.97,5.17-21.93,8.61-32.9v-.76Z"/><path fill="#F27182" d="M266.88,103.3v-3.03h-77.09c-2.15-9.83-3.44-19.66-3.44-29.12,0-19.66,7.75-34.03,28.84-34.03,18.54,0,41.78,12.1,56.85,26.09V15.58c-31.43-.38-56.85,6.81-75.36,20.8-20.23,15.5-31.43,38.95-31.43,70.33,0,63.53,37.45,118.35,107.22,156.17v-50.67c-40.9-24.96-71.02-67.53-81.79-108.75l76.19-.15Z"/><path fill="#F27182" d="M370.63,17.47c1.29,6.05,2.58,12.48,3.44,18.53,7.32,45.38,7.32,99.83-1.29,145.2l-38.74-138.02c-3.44-11.34-5.6-18.15-5.17-25.71v-.38h-48.65v.38c2.15,3.03,4.3,9.83,8.61,24.2l63.28,221.2h3.44l58.12-232.17,3.44-13.23v-.38h-46.49v.38Z"/><path fill="#F27182" d="M538.53,17.09h-14.64c-27.55,0-49.94,10.97-68.02,29.49-37.02,38.57-53.38,110.41-51.23,188.31.43,13.99.43,21.55-2.15,27.6v.38h52.09v-.38c-2.15-7.94-3.44-15.13-4.3-27.6-3.87-54.07-4.3-94.91-.86-125.92h40.9v143.31c0,4.54-.86,8.32-5.17,10.21v.38h53.38v-.38c-4.3-1.89-5.17-5.67-5.17-10.21V27.68c0-4.54.86-8.32,5.17-10.21v-.38ZM449.41,105.95c3.01-24.96,9.04-43.48,18.08-57.1,6.03-9.08,13.35-15.88,22.82-21.18v78.27h-40.9Z"/><path fill="#F27182" d="M696.52,17.09h-57.26v.76c6.46,10.21,12.05,20.42,16.79,30.63,15.5,33.28,21.1,68.06,23.68,122.51l-76.2-153.9h-51.23v.76c12.05,16.26,17.22,28.36,17.22,57.1,0,33.28-2.58,105.88-6.89,156.54l-2.58,31.01v.38h57.26v-.38c-4.73-10.21-9.9-21.93-13.34-30.63-23.25-58.61-31-110.03-31-181.12l104.61,212.13h11.62c-.43-7.56-5.6-94.91-7.32-132.34-1.29-25.33.43-52.94,6.03-79.78,2.15-10.97,5.17-21.93,8.61-32.9v-.76Z"/><path fill="#F27182" d="M772.29,265.14c36.16,0,51.23-18.15,51.23-52.18V42.8c0-10.59,3.44-19.28,11.62-25.33v-.38h-26.69v.38c8.18,6.05,11.62,14.75,11.62,25.33v172.05c0,19.28-7.75,40.84-30.57,40.84s-30.13-18.53-30.13-39.7V27.68c0-4.54.86-8.32,5.17-10.21v-.38h-53.38v.38c4.3,1.89,5.17,5.67,5.17,10.21v176.58c0,35.54,17.22,60.88,55.96,60.88Z"/></g></svg>';

// ============================================================
// SESSION PERSISTENCE & INACTIVITY
// ============================================================

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let lastSessionSave = 0;

function resetInactivityTimer(): void {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  const now = Date.now();
  if (now - lastSessionSave > 30000) {
    saveSession();
    lastSessionSave = now;
  }
  inactivityTimer = setTimeout(() => {
    showToast('Sitzung abgelaufen — bitte erneut anmelden');
    logoutPin(true);
  }, INACTIVITY_TIMEOUT);
}

function startInactivityTracking(): void {
  const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'input', 'click'];
  for (const ev of events) {
    document.addEventListener(ev, resetInactivityTimer, { passive: true });
  }
  resetInactivityTimer();
}

function restoreSession(): any {
  try {
    const raw = localStorage.getItem('nevanu_session');
    if (!raw) return null;
    const session = JSON.parse(raw);
    // B6 Schritt 4, Vorbereitung (S2, 09.10.2026): eine Sitzung ohne PIN ist gültig (tools/b6-pin-frei-wache.cjs)
    if (!session?.user || !session?.timestamp) return null;
    if (Date.now() - session.timestamp > INACTIVITY_TIMEOUT) {
      clearSession();
      return null;
    }
    return session.user;
  } catch {
    clearSession();
    return null;
  }
}

// ============================================================
// LOGIN
// ============================================================

function loginSubmit(e: Event): void {
  e.preventDefault();
  const email = (document.getElementById('login-email') as HTMLInputElement)?.value?.trim();
  const password = (document.getElementById('login-password') as HTMLInputElement)?.value;
  if (!email || !password) return;

  const btn = document.getElementById('login-btn') as HTMLButtonElement;
  if (btn) { btn.disabled = true; btn.textContent = 'Anmelden...'; }
  const errEl = document.getElementById('pin-error');
  if (errEl) errEl.innerHTML = '&nbsp;';

  api<{ ok: boolean; user?: any; error?: string }>('team', 'login', {}, { email, password })
    .then(data => {
      if (btn) { btn.disabled = false; btn.textContent = 'Anmelden'; }
      if (data.ok && data.user) {
        (window as any).currentUser = {
          pin: data.user.pin,
          name: data.user.name,
          role: data.user.role || 'stylist',
          id: data.user.id || null,
          email: data.user.email || '',
        };
        saveSession();
        startInactivityTracking();
        const overlay = document.getElementById('pin-overlay');
        if (overlay) {
          overlay.classList.add('hidden');
          setTimeout(() => { overlay.style.display = 'none'; }, 400);
        }
        afterPinSuccess();
      } else {
        loginError(data.error || 'Anmeldung fehlgeschlagen');
      }
    })
    .catch(() => {
      if (btn) { btn.disabled = false; btn.textContent = 'Anmelden'; }
      loginError('Verbindungsfehler — bitte erneut versuchen');
    });
}

function loginError(msg: string): void {
  const el = document.getElementById('pin-error');
  if (el) el.textContent = msg || 'E-Mail oder Passwort falsch';
}

function logoutPin(keepFormData?: boolean): void {
  (window as any).currentUser = null;
  clearSession();
  if (typeof (window as any).stopBadgePolling === 'function') (window as any).stopBadgePolling();
  try {
    sessionStorage.removeItem('nevanu_dashTab');
    sessionStorage.removeItem('nevanu_inApp');
  } catch { /* */ }

  // Inactivity Timer stoppen
  if ((window as any)._inactivityTimer) {
    clearTimeout((window as any)._inactivityTimer);
    (window as any)._inactivityTimer = null;
  }

  // Cached Data leeren
  (window as any)._zeitStatus = null;
  (window as any)._homeDataCache = null;
  (window as any)._homeDataCacheTime = 0;
  (window as any)._profilData = null;

  // Bottom Nav entfernen (Lava BG bleibt global)
  const nav = document.getElementById('bottom-nav');
  if (nav) nav.classList.add('hidden');

  // Dashboard entfernen
  const dash = document.getElementById('dashboard');
  if (dash) dash.remove();

  // App-View ausblenden
  const app = document.getElementById('app');
  if (app) app.style.display = 'none';

  // Login-Overlay anzeigen
  const overlay = document.getElementById('pin-overlay');
  if (overlay) {
    overlay.style.display = '';
    overlay.classList.remove('hidden');
  }

  // === Login-Sub-Forms zurücksetzen ===
  // Alle Sub-Forms verstecken
  const subForms = ['set-pw-form', 'reset-pw-form', 'confirm-reset-form', 'magic-login-form', 'magic-verify-status'];
  for (const id of subForms) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  // Login-Formular anzeigen + zurücksetzen
  const loginForm = document.getElementById('login-form') as HTMLFormElement | null;
  if (loginForm) {
    loginForm.style.display = '';
    loginForm.reset();
  }
  // Links wieder anzeigen
  const links = document.querySelector('#pin-overlay .pin-box > div[style*="text-align:center"]');
  if (links) (links as HTMLElement).style.display = '';
  // Fehler leeren
  const pinErr = document.getElementById('pin-error');
  if (pinErr) pinErr.innerHTML = '&nbsp;';
  // Sub-Form Messages leeren
  const msgIds = ['magic-login-msg', 'magic-verify-msg', 'set-pw-msg', 'reset-pw-msg', 'confirm-reset-msg'];
  for (const id of msgIds) {
    const el = document.getElementById(id);
    if (el) { el.textContent = ''; el.style.color = ''; }
  }

  // Form-Daten löschen (wenn nicht beibehalten)
  if (!keepFormData && typeof (window as any).resetFormData === 'function') {
    (window as any).resetFormData();
  }

  activeDashTab = 'home';
}

// ============================================================
// PORTAL-Detection — aus NEVANU_CONFIG (window) abgeleitet
// Server/Domain-Wechsel aenderbar ueber config.php, kein Code-Change noetig
// ============================================================

const isPortalDomain: boolean = (function(): boolean {
  const h = window.location.hostname;
  const cfg = (window as any).NEVANU_CONFIG || {};
  const portalHosts: string[] = [];
  try {
    if (cfg.adminBase) portalHosts.push(new URL(cfg.adminBase).hostname);
    if (cfg.apiBase)   portalHosts.push(new URL(cfg.apiBase).hostname);
  } catch (e) { /* ignore */ }
  // Legacy-Fallback falls Config fehlt
  const fallback = ['my.nevanu.ch', 'api.nevanu.ch', 'admin.nevanu.ch', 'my.nev.li'];
  const hosts = portalHosts.length ? portalHosts : fallback;
  return hosts.indexOf(h) !== -1;
})();

function afterPinSuccess(): void {
  const user = (window as any).currentUser;
  if (isPortalDomain && user?.role === 'admin') {
    let inApp = false;
    try { inApp = sessionStorage.getItem('nevanu_inApp') === '1'; } catch { /* */ }
    if (inApp) {
      showDashboard();
    } else {
      showAdminPortal();
    }
  } else {
    showDashboard();
  }
}

function showAdminPortal(): void {
  const p = document.getElementById('admin-portal');
  if (p) {
    p.style.display = 'block';
    p.style.opacity = '0';
    setTimeout(() => { p.style.opacity = '1'; }, 20);
  }
}

function portalGoApp(): void {
  const p = document.getElementById('admin-portal');
  if (p) p.style.display = 'none';
  try { sessionStorage.setItem('nevanu_inApp', '1'); } catch { /* */ }
  showDashboard();
}

function portalLogout(): void {
  const p = document.getElementById('admin-portal');
  if (p) p.style.display = 'none';
  logoutPin();
}

// ============================================================
// DASHBOARD SHELL
// ============================================================

function showDashboard(): void {
  const app = document.getElementById('app');
  if (app) app.style.display = 'none';

  const existing = document.getElementById('dashboard');
  if (existing) {
    existing.style.display = 'block';
    showBottomNav();
    updateBottomNavActive();
    _injectDesktopSidebar();
    loadDashboard();
    requestAnimationFrame(() => initNevaNavCanvas());
    if (typeof (window as any).startBadgePolling === 'function') (window as any).startBadgePolling();
    if (typeof (window as any).updateThemeIcon === 'function') (window as any).updateThemeIcon();
    return;
  }

  // Dashboard erstellen
  const d = document.createElement('div');
  d.id = 'dashboard';
  d.style.display = 'block';
  d.innerHTML = buildDashboardHTML();
  document.body.appendChild(d);
  _injectDesktopSidebar();
  updateBottomNavActive();
  loadDashboard();
  // Neva Nav Canvas Animation starten
  requestAnimationFrame(() => initNevaNavCanvas());
  if (typeof (window as any).startBadgePolling === 'function') (window as any).startBadgePolling();
  if (typeof (window as any).updateThemeIcon === 'function') (window as any).updateThemeIcon();
  if (typeof (window as any).preloadProfilBild === 'function') (window as any).preloadProfilBild();
}

// ============================================================
// DESKTOP SIDEBAR (Instagram-Style)
// ============================================================

function _injectDesktopSidebar(): void {
  if (document.getElementById('nv-sidebar')) return;
  if (window.innerWidth < 769) return;

  // Try form.js version first (has full nav items + theme toggle)
  if (typeof (window as any).buildDesktopSidebarHTML === 'function') {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = (window as any).buildDesktopSidebarHTML();
    const sidebar = wrapper.firstChild;
    if (sidebar) document.body.insertBefore(sidebar, document.body.firstChild);
    document.body.classList.add('has-sidebar');
    return;
  }

  // Fallback: build basic sidebar here (form.js not loaded yet) — Sachemo design
  const user = (window as any).currentUser;
  const role = user?.role || 'stylist';
  const isAdmin = role === 'admin' || role === 'inhaber';
  const isManagerRole = role === 'admin' || role === 'inhaber' || role === 'manager';
  const userName = user?.name || '';
  const userInitials = userName ? userName.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase() : '';

  function si(id: string, icon: string, label: string): string {
    return `<div class="nv-sidebar-item" data-nav="${id}" data-tooltip="${escHtml(label)}" onclick="_sidebarNav('${id}');event.stopPropagation()">` +
      `<span class="material-symbols-outlined">${icon}</span>` +
      `<span class="nv-sidebar-item-label">${escHtml(label)}</span></div>`;
  }
  function sec(id: string, label: string): string {
    return `<div class="nv-sidebar-section" data-section="${id}" onclick="_toggleSidebarSection('${id}');event.stopPropagation()">${label}<span class="material-symbols-outlined nv-sidebar-section-arrow" data-symbol="expand_more">&#xe5cf;</span></div><div class="nv-sidebar-group" data-section-group="${id}">`;
  }

  let h = '<aside class="nv-sidebar" id="nv-sidebar" onclick="_sidebarExpandOnCollapsed(event)">';
  // Header: Logo + Hamburger
  h += '<div class="nv-sidebar-header">';
  h += '<span class="nv-sidebar-logo-mini" style="font-size:1.4rem;font-weight:900;color:var(--nv-akzent)">N.</span>';
  h += '<span class="nv-sidebar-logo-full"><span style="font-size:1.1rem;font-weight:900;letter-spacing:-.5px">NEVANU</span></span>';
  h += '<button class="nv-sidebar-toggle" onclick="_toggleSidebar();event.stopPropagation()"><span class="material-symbols-outlined" data-symbol="menu">&#xe5d2;</span></button>';
  h += '</div>';
  h += '<nav class="nv-sidebar-nav">';

  // Registry-based sidebar: read from nav-registry.js
  const navItems: any[] = (window as any).NAV_ITEMS || [];
  const navSections: any[] = (window as any).NAV_SECTIONS || [];

  for (const section of navSections) {
    // Section-Rolle prüfen
    if (section.role === 'manager' && !isManagerRole) continue;
    if (section.role === 'admin' && !isAdmin) continue;

    const items = navItems.filter((item: any) => {
      if (item.section !== section.id) return false;
      if (!item.role || item.role === 'all') return true;
      if (item.role === 'manager') return isManagerRole;
      if (item.role === 'admin') return isAdmin;
      return false;
    });

    if (items.length === 0) continue;
    // Website-Section traegt den Namen der aktiven Site („Website nevanu.ch").
    const secLabel = typeof (window as any).getNavSectionLabel === 'function'
      ? (window as any).getNavSectionLabel(section) : section.label;
    h += sec(section.id, secLabel);
    for (const item of items) {
      h += si(item.id, item.icon, item.label);
    }
    h += '</div>';
  }

  h += '</nav>';
  // Footer: Theme + Profile (Sachemo)
  h += '<div class="nv-sidebar-footer">';
  h += `<div class="nv-sidebar-item" data-tooltip="Theme" onclick="cycleThemeMode();event.stopPropagation()"><span class="material-symbols-outlined" data-symbol="brightness_auto">&#xe1ab;</span><span class="nv-sidebar-item-label">Theme</span></div>`;
  h += `<div class="nv-sidebar-profile" onclick="event.stopPropagation()">`;
  h += `<div class="nv-sidebar-avatar">${escHtml(userInitials)}</div>`;
  h += `<div class="nv-sidebar-profile-info"><div class="nv-sidebar-profile-name">${escHtml(userName)}</div></div>`;
  h += `</div>`;
  h += '</div></aside>';

  const wrapper = document.createElement('div');
  wrapper.innerHTML = h;
  const sidebar = wrapper.firstChild;
  if (sidebar) document.body.insertBefore(sidebar, document.body.firstChild);
  document.body.classList.add('has-sidebar');
}

// Sidebar navigation handler — delegates to nav-registry navTo()
function _sidebarNav(id: string): void {
  if (typeof (window as any).navTo === 'function') {
    (window as any).navTo(id);
    _updateSidebarActive();
    return;
  }
  // Legacy fallback
  if (typeof (window as any)._sidebarNavLegacy === 'function') {
    (window as any)._sidebarNavLegacy(id);
    _updateSidebarActive();
  }
}

function _updateSidebarActive(): void {
  // Sync mit form.js globalem activeDashTab
  const globalTab = (window as any).activeDashTab;
  if (globalTab && typeof globalTab === 'string') activeDashTab = globalTab;

  const items = document.querySelectorAll('.nv-sidebar-item[data-nav]');
  items.forEach(item => {
    const navId = item.getAttribute('data-nav');
    if (navId) item.classList.toggle('active', navId === activeDashTab);
  });
}

// Nur setzen wenn form.js diese nicht bereits definiert hat
if (typeof (window as any)._sidebarNav !== 'function') (window as any)._sidebarNav = _sidebarNav;
if (typeof (window as any)._updateSidebarActive !== 'function') (window as any)._updateSidebarActive = _updateSidebarActive;

// Resize handler: toggle sidebar on desktop/mobile switch
window.addEventListener('resize', () => {
  const sb = document.getElementById('nv-sidebar');
  if (window.innerWidth < 769) {
    if (sb) { sb.remove(); document.body.classList.remove('has-sidebar'); }
  } else {
    if (!sb && document.getElementById('dashboard')) _injectDesktopSidebar();
  }
});

// ============================================================
// DELEGATE TO form.js — KEINE eigenen HTML-Builder mehr!
// form.js ist die Single Source of Truth für Dashboard/Header/Bottom Bar.
// shell.ts überschrieb bisher form.js Änderungen — das ist JETZT VORBEI.
// ============================================================

function buildDashboardHTML(): string {
  // Delegiere komplett an form.js
  const fn = (window as any).buildDashboardHTML;
  if (typeof fn === 'function') return fn();
  // Fallback: minimales HTML damit die App nicht bricht
  return '<div style="font-family:var(--font)"><div id="dash-content" style="padding:16px"></div></div>';
}

function loadDashboard(): void {
  // Delegiere an form.js
  const fn = (window as any).loadDashboard;
  if (typeof fn === 'function') { fn(); return; }
  // Fallback: TS-Router
  const container = document.getElementById('dash-content');
  if (!container) return;
  const globalTab = (window as any).activeDashTab;
  if (globalTab && typeof globalTab === 'string' && globalTab !== activeDashTab) {
    activeDashTab = globalTab;
  }
  navigateTo(activeDashTab, container);
}

function buildBottomNavHTML(): string {
  // Delegiere an form.js
  const fn = (window as any).buildBottomNavHTML;
  if (typeof fn === 'function') return fn();
  return '';
}

// ============================================================
// DOCK — kein Magnification mehr, Gradient-Expand via CSS
// ============================================================

function initDockMagnification(): void {
  // Nicht mehr benötigt — alles via CSS transition
  // Funktion bleibt als No-Op damit bestehende Aufrufe nicht brechen
}

function updateBottomNavActive(): void {
  const navItems = document.querySelectorAll('.bottom-nav-item');
  navItems.forEach(item => {
    // Neva center button — nie "active" styling
    if (item.classList.contains('bottom-nav-neva')) return;
    const id = item.id.replace('bnav-', '');
    let isActive = false;
    if (id === 'stempeln' && activeDashTab === 'zeiterfassung') isActive = true;
    else if (id === activeDashTab) isActive = true;
    item.className = 'bottom-nav-item' + (isActive ? ' active' : '');
  });

  // "Mehr" aktiv wenn aktueller Tab darin enthalten
  if (IN_MORE_TABS.includes(activeDashTab)) {
    const mehrEl = document.getElementById('bnav-mehr');
    if (mehrEl) mehrEl.className = 'bottom-nav-item active';
  }

  updateStempelnNavState();
}

function bottomNavGo(id: string): void {
  if (id === 'rechner') {
    bottomSheetOpen = false;
    closeAllSheets();
    if (typeof (window as any).openQuickRechner === 'function') (window as any).openQuickRechner();
    return;
  }
  bottomSheetOpen = false;
  closeAllSheets();
  (window as any)._homeDataCache = null;
  (window as any)._homeDataCacheTime = 0;
  activeDashTab = id;
  (window as any).activeDashTab = id;
  try { sessionStorage.setItem('nevanu_dashTab', id); } catch { /* */ }
  updateBottomNavActive();
  loadDashboard();
  window.scrollTo(0, 0);
}

function showBottomNav(): void {
  const el = document.getElementById('bottom-nav');
  if (el) el.classList.remove('hidden');
}

function hideBottomNav(): void {
  const el = document.getElementById('bottom-nav');
  if (el) el.classList.add('hidden');
}

function updateStempelnNavState(): void {
  const stempBtn = document.getElementById('bnav-stempeln');
  if (!stempBtn) return;
  const label = stempBtn.querySelector('.bottom-nav-label');
  const zeitStatus = (window as any)._zeitStatus;
  const isEin = zeitStatus?.status === 'eingestempelt';
  if (isEin) {
    stempBtn.classList.add('stempeln-aktiv');
    if (label) label.textContent = 'Aktiv';
  } else {
    stempBtn.classList.remove('stempeln-aktiv');
    if (label) label.textContent = 'Stempeln';
  }
}

// ============================================================
// CLOSE ALL SHEETS
// ============================================================

function closeAllSheets(): void {
  bottomSheetOpen = false;
  const ids = [
    'leistung-editor-overlay', 'kategorie-editor-overlay', 'zusatz-editor-overlay',
    'team-leistung-overlay', 'ausnahme-editor-overlay', 'termin-detail-overlay',
    'termin-erstellen-overlay', 'mehr-overlay', 'more-sheet-overlay',
    'kunden-profil-overlay', 'profil-sheet-overlay',
  ];
  for (const id of ids) {
    const ov = document.getElementById(id);
    if (ov?.parentNode) ov.parentNode.removeChild(ov);
  }
  document.querySelectorAll('.bottom-sheet-overlay').forEach(ov => {
    if (ov.parentNode) ov.parentNode.removeChild(ov);
  });
  document.body.style.overflow = '';
}

// ============================================================
// MORE SHEET
// ============================================================

function openMoreSheet(): void {
  if (bottomSheetOpen) return;
  bottomSheetOpen = true;

  const session = getSession();
  const role = session?.role || 'stylist';
  const isAdmin = role === 'admin' || role === 'inhaber';
  const isManagerRole = role === 'manager' || isAdmin;

  const ov = document.createElement('div');
  ov.id = 'more-sheet-overlay';
  ov.className = 'bottom-sheet-overlay';
  ov.onclick = (e) => { if (e.target === ov) closeMoreSheet(); };

  let h = '<div class="bottom-sheet">';
  h += '<div class="bottom-sheet-handle"></div>';
  h += '<div class="bottom-sheet-title">Menü</div>';
  h += '<div class="bottom-sheet-items">';

  // Registry-based more sheet: read from nav-registry.js
  const navItems: any[] = (window as any).NAV_ITEMS || [];
  const navSections: any[] = (window as any).NAV_SECTIONS || [];
  const getNavBadge = (window as any).getNavBadge;

  for (const section of navSections) {
    // Section-Rolle prüfen
    if (section.role === 'manager' && !isManagerRole) continue;
    if (section.role === 'admin' && !isAdmin) continue;

    const items = navItems.filter((item: any) => {
      if (item.section !== section.id) return false;
      if (!item.role || item.role === 'all') return true;
      if (item.role === 'manager') return isManagerRole;
      if (item.role === 'admin') return isAdmin;
      return false;
    });

    if (items.length === 0) continue;

    // Website-Section traegt den Namen der aktiven Site („Website nevanu.ch").
    const sheetSecLabel = typeof (window as any).getNavSectionLabel === 'function'
      ? (window as any).getNavSectionLabel(section) : section.label;
    h += `<div class="bottom-sheet-section-title">${escHtml(sheetSecLabel)}</div>`;
    for (const item of items) {
      const navActiveClass = activeDashTab === item.id ? ' active' : '';
      h += `<div class="bottom-sheet-item${navActiveClass}" onclick="moreSheetNav('${item.id}')">`;
      h += `<span class="material-symbols-outlined bsi-icon">${item.icon}</span>`;
      h += `<span class="bsi-label">${escHtml(item.label)}</span>`;
      // Badge
      const badge = typeof getNavBadge === 'function' ? getNavBadge(item.id) : 0;
      if (badge > 0) {
        const badgeStyle = item.id === 'lager' ? ' style="background:var(--k-yellow)"' : '';
        h += `<span class="bsi-badge"${badgeStyle}>${badge}</span>`;
      }
      h += '</div>';
    }
    h += '<div class="bottom-sheet-divider"></div>';
  }

  // Profil
  h += `<div class="bottom-sheet-item" onclick="moreSheetNav('profil')">`;
  h += '<span class="material-symbols-outlined bsi-icon" data-symbol="account_circle">&#xe853;</span>';
  h += '<span class="bsi-label">Mein Profil</span></div>';

  // Theme
  const THEME_MODE_KEY = 'nevanu_theme_mode';
  const mode = localStorage.getItem(THEME_MODE_KEY) || 'auto';
  const modeLabel: Record<string, string> = { auto: 'Auto', light: 'Hell', dark: 'Dunkel', mono: 'Mono' };
  const modeIcon: Record<string, string> = { auto: 'brightness_auto', light: 'light_mode', dark: 'dark_mode', mono: 'contrast' };
  h += `<div class="bottom-sheet-item" onclick="_cycleThemeInSheet('more')">`;
  h += `<span class="material-symbols-outlined bsi-icon" id="more-theme-icon">${modeIcon[mode] || 'brightness_auto'}</span>`;
  h += '<span class="bsi-label">Theme wechseln</span>';
  h += `<span id="more-theme-label" style="margin-left:auto;font-size:.72rem;color:var(--text3);font-weight:600">${modeLabel[mode] || 'Auto'}</span>`;
  h += '</div>';

  h += '<div class="bottom-sheet-divider"></div>';

  // Logout
  h += '<div class="bottom-sheet-item danger" onclick="closeMoreSheet();logoutPin()">';
  h += '<span class="material-symbols-outlined bsi-icon" data-symbol="logout">&#xe9ba;</span>';
  h += '<span class="bsi-label">Abmelden</span></div>';

  h += '</div></div>';
  ov.innerHTML = h;
  document.body.appendChild(ov);
  requestAnimationFrame(() => requestAnimationFrame(() => ov.classList.add('active')));
  addBottomSheetSwipe(ov, closeMoreSheet);
}

function closeMoreSheet(): void {
  bottomSheetOpen = false;
  const ov = document.getElementById('more-sheet-overlay');
  if (ov) {
    ov.classList.remove('active');
    setTimeout(() => { if (ov.parentNode) ov.remove(); }, 300);
  }
}

// moreSheetNav — delegates to nav-registry navTo()
function moreSheetNav(id: string): void {
  bottomSheetOpen = false;
  closeAllSheets();
  if (typeof (window as any).navTo === 'function') {
    (window as any).navTo(id);
    return;
  }
  // Legacy fallback
  activeDashTab = id;
  (window as any).activeDashTab = id;
  try { sessionStorage.setItem('nevanu_dashTab', id); } catch { /* */ }
  updateBottomNavActive();
  loadDashboard();
  window.scrollTo(0, 0);
}

// ============================================================
// PROFIL BOTTOM SHEET (Avatar Click)
// ============================================================

function openProfilSheet(): void {
  if (bottomSheetOpen) return;
  bottomSheetOpen = true;

  const user = (window as any).currentUser;
  const name = user?.name || '';
  const vorname = name.split(' ')[0] || name;
  const role = user?.role || '';
  const roleLabel = getRoleLabel(role);
  const profilData = (window as any)._profilData;
  const profilBild = profilData?.profilbild || '';

  const ov = document.createElement('div');
  ov.id = 'profil-sheet-overlay';
  ov.className = 'bottom-sheet-overlay';
  ov.onclick = (e) => { if (e.target === ov) closeProfilSheet(); };

  let h = '<div class="bottom-sheet" style="max-height:70vh">';
  h += '<div class="bottom-sheet-handle"></div>';
  h += '<div style="padding:16px 20px 8px;text-align:center">';

  // Avatar
  h += '<div class="profil-sheet-avatar">';
  if (profilBild) h += `<img src="${escAttr(profilBild)}" alt="Profilbild">`;
  else h += escHtml(vorname.charAt(0).toUpperCase());
  h += '</div>';
  h += `<div class="profil-sheet-name">${escHtml(name)}</div>`;
  h += `<div class="profil-sheet-role">${roleLabel}</div>`;
  h += '</div>';

  // Quick stats
  h += '<div class="profil-sheet-stats">';
  h += '<div class="profil-sheet-stat"><div class="profil-sheet-stat-num" id="ps-stat-week">-</div><div class="profil-sheet-stat-label">Woche</div></div>';
  h += '<div class="profil-sheet-stat"><div class="profil-sheet-stat-num" id="ps-stat-month">-</div><div class="profil-sheet-stat-label">Monat</div></div>';
  h += '<div class="profil-sheet-stat"><div class="profil-sheet-stat-num" id="ps-stat-open">-</div><div class="profil-sheet-stat-label">Offen</div></div>';
  h += '</div>';

  // Menu items
  h += '<div class="bottom-sheet-items">';
  h += `<div class="bottom-sheet-item" onclick="closeProfilSheet();menuNavigate('profil')">`;
  h += '<span class="material-symbols-outlined bsi-icon" data-symbol="person">&#xe7fd;</span>';
  h += '<span class="bsi-label">Persönliche Daten</span></div>';
  h += `<div class="bottom-sheet-item" onclick="closeProfilSheet();menuNavigate('zeiterfassung')">`;
  h += '<span class="material-symbols-outlined bsi-icon" data-symbol="schedule">&#xe192;</span>';
  h += '<span class="bsi-label">Zeiterfassung</span></div>';
  h += '<div class="bottom-sheet-divider"></div>';

  // Bug-Modus
  const bugActive = !!(window as any)._bugModeActive;
  h += `<div class="bottom-sheet-item" id="profil-bugmode-btn" onclick="_toggleBugMode()" style="${bugActive ? 'background:rgba(236,64,122,.12)' : ''}">`;
  h += `<span class="material-symbols-outlined bsi-icon" style="${bugActive ? 'color:#EC407A' : ''}" data-symbol="bug_report">&#xe868;</span>`;
  h += `<span class="bsi-label" style="${bugActive ? 'color:#EC407A;font-weight:700' : ''}">Bug-Modus</span>`;
  h += `<span id="profil-bugmode-status" style="margin-left:auto;font-size:.7rem;font-weight:600;padding:3px 10px;border-radius:20px;${bugActive ? 'background:#EC407A;color:#fff' : 'background:var(--border);color:var(--text3)'}">${bugActive ? 'AN' : 'AUS'}</span>`;
  h += '</div>';

  h += '<div class="bottom-sheet-divider"></div>';
  h += '<div class="bottom-sheet-item danger" onclick="closeProfilSheet();logoutPin()">';
  h += '<span class="material-symbols-outlined bsi-icon" data-symbol="logout">&#xe9ba;</span>';
  h += '<span class="bsi-label">Abmelden</span></div>';
  h += '</div></div>';

  ov.innerHTML = h;
  document.body.appendChild(ov);
  requestAnimationFrame(() => requestAnimationFrame(() => ov.classList.add('active')));
  addBottomSheetSwipe(ov, closeProfilSheet);

  // Stats laden
  fetchProfilSheetStats();
}

function closeProfilSheet(): void {
  bottomSheetOpen = false;
  const ov = document.getElementById('profil-sheet-overlay');
  if (ov) {
    ov.classList.remove('active');
    setTimeout(() => { if (ov.parentNode) ov.remove(); }, 300);
  }
}

async function fetchProfilSheetStats(): Promise<void> {
  try {
    const data = await api<{ ok: boolean; week?: number; month?: number; open?: number }>('todos', 'profile_stats', {}, { action: 'profile_stats' });
    if (data.ok) {
      const w = document.getElementById('ps-stat-week');
      const m = document.getElementById('ps-stat-month');
      const o = document.getElementById('ps-stat-open');
      if (w) w.textContent = String(data.week || 0);
      if (m) m.textContent = String(data.month || 0);
      if (o) o.textContent = String(data.open || 0);
    }
  } catch { /* silent */ }
}

// ============================================================
// HEADER FAB — Speed Dial
// ============================================================

function toggleHeaderFab(e?: Event): void {
  if (e) e.stopPropagation();
  const existing = document.getElementById('header-fab-menu');
  if (existing) { existing.remove(); headerFabOpen = false; return; }
  headerFabOpen = true;

  const user = (window as any).currentUser;
  const isEmployee = user && !isManager();

  let items: FabItem[];
  if (isEmployee) {
    items = [
      { icon: 'event', label: 'Termin', action: `openTerminErstellen(new Date().toISOString().split("T")[0],${user.id || null})` },
      { icon: 'assignment', label: 'Beratung', action: 'switchToForm()' },
      { icon: 'free_cancellation', label: 'Pause', action: `if(typeof _kalSuppressScroll!=="undefined")_kalSuppressScroll=true;menuNavigate("buchungen");setTimeout(function(){if(typeof _kalAddPause==="function"){var d=new Date().toISOString().split("T")[0];_kalAddPause(d,null,${user.id || null})}},400)` },
    ];
  } else {
    items = [
      { icon: 'event', label: 'Termin', action: 'openTerminErstellen(new Date().toISOString().split("T")[0])' },
      { icon: 'person_add', label: 'Kunde', action: 'menuNavigate("kunden");setTimeout(function(){if(typeof _kundenNeuOpen==="function")_kundenNeuOpen()},300)' },
      { icon: 'assignment', label: 'Beratung', action: 'switchToForm()' },
      { icon: 'add_task', label: 'Task', action: 'menuNavigate("aufgaben");setTimeout(function(){if(typeof _taskCreate==="function")_taskCreate()},300)' },
    ];
  }

  const m = document.createElement('div');
  m.id = 'header-fab-menu';
  m.style.cssText = 'position:fixed;inset:0;z-index:9990';

  const bg = document.createElement('div');
  bg.style.cssText = 'position:absolute;inset:0;background:rgba(0,0,0,.25);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px)';
  bg.onclick = () => { m.remove(); headerFabOpen = false; };
  m.appendChild(bg);

  const fabColor = isEmployee ? '#34C759' : 'var(--nv-akzent)';
  const bbl = document.createElement('div');
  bbl.style.cssText = 'position:absolute;top:60px;right:16px;display:flex;flex-direction:column;gap:8px;animation:fadeIn .2s ease';

  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const row = document.createElement('div');
    row.style.cssText = `display:flex;align-items:center;gap:10px;justify-content:flex-end;animation:fadeIn .2s ease;animation-delay:${i * 0.05}s`;

    const lbl = document.createElement('span');
    lbl.style.cssText = 'background:rgba(26,26,26,.85);color:#fff;padding:6px 14px;border-radius:var(--radius-full);font-size:.78rem;font-weight:600;font-family:var(--font);white-space:nowrap;-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);box-shadow:0 4px 16px rgba(0,0,0,.15)';
    lbl.textContent = it.label;

    const btn = document.createElement('button');
    btn.style.cssText = `width:46px;height:46px;border-radius:50%;border:none;background:rgba(255,255,255,.85);-webkit-backdrop-filter:blur(16px) saturate(180%);backdrop-filter:blur(16px) saturate(180%);color:${fabColor};cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(0,0,0,.12),0 0 0 1px rgba(255,255,255,.3) inset;transition:transform .15s;flex-shrink:0`;
    btn.innerHTML = `<span class="material-symbols-outlined" style="font-size:22px">${it.icon}</span>`;
    btn.setAttribute('data-action', it.action);
    btn.onclick = function () {
      const a = this.getAttribute('data-action');
      m.remove();
      headerFabOpen = false;
      if (a) (0, eval)(a); // eslint-disable-line no-eval
    };

    row.appendChild(lbl);
    row.appendChild(btn);
    bbl.appendChild(row);
  }
  m.appendChild(bbl);
  document.body.appendChild(m);
}

// ============================================================
// LEGACY NAVIGATION FUNCTIONS
// ============================================================

function menuNavigate(id: string): void {
  (window as any)._homeDataCache = null;
  (window as any)._homeDataCacheTime = 0;
  bottomSheetOpen = false;
  closeAllSheets();
  if (id === 'logout') { logoutPin(); return; }
  activeDashTab = id;
  (window as any).activeDashTab = id;
  try { sessionStorage.setItem('nevanu_dashTab', id); } catch { /* */ }
  updateBottomNavActive();
  loadDashboard();
  window.scrollTo(0, 0);
}

function dashTab(tab: string): void {
  activeDashTab = tab;
  (window as any).activeDashTab = tab;
  try { sessionStorage.setItem('nevanu_dashTab', tab); } catch { /* */ }
  loadDashboard();
}

// ============================================================
// TEAM PINS LOADING
// ============================================================

function loadTeamPins(callback?: () => void): void {
  api<{ ok: boolean; team?: any[] }>('team', 'list', {})
    .then(data => {
      if (data.ok && data.team) {
        const pins: Record<string, any> = {};
        const list: any[] = [];
        for (const m of data.team) {
          if (m.pin) pins[m.pin] = m;
          list.push(m);
        }
        (window as any).TEAM_PINS = pins;
        (window as any).TEAM_LIST = list;
      }
    })
    .catch(() => {})
    .finally(() => { if (callback) callback(); });
}

// ============================================================
// INIT (DOMContentLoaded)
// ============================================================

function initApp(): void {
  // Team PINs laden
  loadTeamPins(() => {
    // Session wiederherstellen
    const restoredUser = restoreSession();
    if (restoredUser) {
      (window as any).currentUser = restoredUser;
      startInactivityTracking();
      const overlay = document.getElementById('pin-overlay');
      if (overlay) {
        overlay.classList.add('hidden');
        setTimeout(() => { overlay.style.display = 'none'; }, 400);
      }
      afterPinSuccess();
    }
  });

  // Auto-Refresh: DEAKTIVIERT in shell.ts — form.js hat eigenen Handler
  // navigateTo('home') loeste das alte TS-Dashboard aus (Race Condition)

  // Scroll-Detection für Header
  window.addEventListener('scroll', () => {
    const header = document.getElementById('dash-header');
    if (header) {
      if (window.scrollY > 20) header.classList.add('scrolled');
      else header.classList.remove('scrolled');
    }
  }, { passive: true });

  // Browser-Back intercepten
  history.replaceState({ nevanu: true }, '', '');
  history.pushState({ nevanu: true }, '', '');
  window.addEventListener('popstate', () => {
    history.pushState({ nevanu: true }, '', '');

    // Form sichtbar? → zurück zum Dashboard
    const app = document.getElementById('app');
    if (app && app.style.display !== 'none') {
      if (typeof (window as any).goBackToDashboard === 'function') (window as any).goBackToDashboard();
      return;
    }

    // Offene Overlays schliessen
    const overlays = [
      'neukunde-detail-overlay', 'protokoll-readonly-overlay',
      'quick-rechner-overlay', 'lager-detail-overlay', 'task-detail-overlay',
    ];
    for (const id of overlays) {
      const ov = document.getElementById(id);
      if (ov) { ov.remove(); return; }
    }

    // Sheets schliessen
    if (document.getElementById('more-sheet-overlay')) { closeMoreSheet(); return; }
    if (document.getElementById('profil-sheet-overlay')) { closeProfilSheet(); return; }

    // Zur Home navigieren
    if (activeDashTab !== 'home') {
      bottomNavGo('home');
    }
  });

  // Theme initialisieren
  initTheme();
}

// ============================================================
// LEGACY BRIDGE
// ============================================================

function exposeGlobals(): void {
  const w = window as any;

  // ============================================================
  // DELEGATION PATTERN:
  // form.js lädt VOR shell.ts (module). Wenn form.js eine Funktion
  // bereits auf window.* definiert hat, überschreiben wir sie NICHT.
  // shell.ts dient nur als Fallback falls form.js noch nicht geladen ist.
  // ============================================================

  function delegateOrFallback(name: string, tsFn: Function): void {
    if (typeof w[name] !== 'function') {
      w[name] = tsFn;
    }
    // else: form.js hat bereits definiert → NICHT überschreiben
  }

  // --- Navigation ---
  delegateOrFallback('buildBottomNavHTML', buildBottomNavHTML);
  delegateOrFallback('updateBottomNavActive', updateBottomNavActive);
  delegateOrFallback('bottomNavGo', bottomNavGo);
  delegateOrFallback('showBottomNav', showBottomNav);
  delegateOrFallback('hideBottomNav', hideBottomNav);
  delegateOrFallback('updateStempelnNavState', updateStempelnNavState);
  delegateOrFallback('_closeAllSheets', closeAllSheets);

  // --- Dashboard ---
  delegateOrFallback('showDashboard', showDashboard);
  delegateOrFallback('buildDashboardHTML', buildDashboardHTML);
  delegateOrFallback('loadDashboard', loadDashboard);
  delegateOrFallback('dashTab', dashTab);

  // --- Desktop Sidebar ---
  delegateOrFallback('_injectDesktopSidebar', _injectDesktopSidebar);

  // --- Auth ---
  delegateOrFallback('logoutPin', logoutPin);
  // loginSubmit, loginError, startInactivityTracking, restoreSession → form.js only
  // loadTeamPins → form.js only (XHR mit Timeout + Fallback)

  // --- Navigation (legacy) ---
  delegateOrFallback('menuNavigate', menuNavigate);

  // --- Portal ---
  delegateOrFallback('showAdminPortal', showAdminPortal);
  delegateOrFallback('portalGoApp', portalGoApp);
  delegateOrFallback('portalLogout', portalLogout);
  delegateOrFallback('_afterPinSuccess', afterPinSuccess);

  // --- v2 Menus: form.js kontrolliert diese komplett ---
  // openMoreSheet, openSalonMenu, openProfilMenu, openAddPopup → form.js only

  // --- activeDashTab sync (Wert, keine Funktion) ---
  // Nur setzen wenn form.js noch keinen Wert hat
  if (typeof w.activeDashTab === 'undefined') {
    w.activeDashTab = activeDashTab;
  }

  // --- Legacy stubs (nur setzen wenn nicht von form.js definiert) ---
  delegateOrFallback('pinInput', () => {});
  delegateOrFallback('pinDelete', () => {});
  delegateOrFallback('updatePinDots', () => {});
  delegateOrFallback('pinError', () => {
    const el = document.getElementById('pin-error');
    if (el) {
      el.textContent = 'Anmeldung fehlgeschlagen';
      setTimeout(() => { el.innerHTML = '&nbsp;'; }, 3000);
    }
  });
  delegateOrFallback('removeLavaBg', () => {});
}

// Neva Nav Canvas Blob Animation — identisch mit Popup (Fourier Harmonics + Rich Gradients)
const PINK_RGB_NAV = '242,113,130';
const PINK_NAV = '#F27182';
let _nevaNavRAF = 0;
function initNevaNavCanvas(): void {
  const c = document.getElementById('neva-nav-canvas') as HTMLCanvasElement | null;
  if (!c) return;
  const ctx = c.getContext('2d');
  if (!ctx) return;
  if (_nevaNavRAF) cancelAnimationFrame(_nevaNavRAF);

  const size = 52;
  const dpr = window.devicePixelRatio || 1;
  c.width = size * dpr; c.height = size * dpr;
  c.style.width = size + 'px'; c.style.height = size + 'px';
  ctx.scale(dpr, dpr);

  const cx = size / 2, cy = size / 2, baseR = size * 0.32;
  const harmonics = [
    { amp: 0.08, freq: 1, phase: 0 },
    { amp: 0.05, freq: 2, phase: 1.2 },
    { amp: 0.03, freq: 3, phase: 2.8 },
    { amp: 0.02, freq: 4, phase: 0.7 },
  ];
  let t = 0;

  function blobRadius(angle: number): number {
    let r = baseR;
    for (let i = 0; i < harmonics.length; i++) {
      const h = harmonics[i];
      r += baseR * h.amp * Math.sin(h.freq * angle + h.phase + t * 0.015 * (i + 1));
    }
    return r;
  }

  function draw(): void {
    t++;
    ctx!.clearRect(0, 0, size, size);
    const steps = 80;

    // Outer neon glow
    const glowR = baseR * 1.4;
    const glow = ctx!.createRadialGradient(cx, cy, baseR * 0.5, cx, cy, glowR);
    glow.addColorStop(0, `rgba(${PINK_RGB_NAV},0)`);
    glow.addColorStop(0.6, `rgba(${PINK_RGB_NAV},.04)`);
    glow.addColorStop(0.85, `rgba(${PINK_RGB_NAV},.02)`);
    glow.addColorStop(1, `rgba(${PINK_RGB_NAV},0)`);
    ctx!.fillStyle = glow; ctx!.fillRect(0, 0, size, size);

    // Blob path
    ctx!.save(); ctx!.beginPath();
    for (let i = 0; i <= steps; i++) {
      const angle = (i / steps) * Math.PI * 2;
      const r = blobRadius(angle);
      const x = cx + Math.cos(angle) * r, y = cy + Math.sin(angle) * r;
      if (i === 0) ctx!.moveTo(x, y); else ctx!.lineTo(x, y);
    }
    ctx!.closePath();

    // Rich gradient
    const blobGrad = ctx!.createRadialGradient(cx - baseR * 0.2, cy - baseR * 0.25, baseR * 0.1, cx, cy, baseR * 1.1);
    blobGrad.addColorStop(0, 'rgba(255,170,180,.95)');
    blobGrad.addColorStop(0.2, `rgba(${PINK_RGB_NAV},.9)`);
    blobGrad.addColorStop(0.5, 'rgba(217,87,106,.75)');
    blobGrad.addColorStop(0.7, 'rgba(139,26,43,.5)');
    blobGrad.addColorStop(1, 'rgba(40,8,16,.3)');
    ctx!.fillStyle = blobGrad; ctx!.fill();

    // Inner light shift
    const lx2 = cx + Math.cos(t * 0.008) * baseR * 0.15;
    const ly2 = cy + Math.sin(t * 0.01) * baseR * 0.15;
    const innerGlow = ctx!.createRadialGradient(lx2, ly2, 0, lx2, ly2, baseR * 0.6);
    innerGlow.addColorStop(0, 'rgba(255,200,210,.25)');
    innerGlow.addColorStop(0.5, `rgba(${PINK_RGB_NAV},.08)`);
    innerGlow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx!.fillStyle = innerGlow; ctx!.fill();

    // Dark accent
    const dx = cx + Math.cos(t * 0.006 + 2) * baseR * 0.2;
    const dy = cy + Math.sin(t * 0.007 + 1) * baseR * 0.25;
    const darkAccent = ctx!.createRadialGradient(dx, dy, 0, dx, dy, baseR * 0.5);
    darkAccent.addColorStop(0, 'rgba(80,10,25,.3)');
    darkAccent.addColorStop(1, 'rgba(0,0,0,0)');
    ctx!.fillStyle = darkAccent; ctx!.fill();

    // Highlight reflection
    const hlGrad = ctx!.createRadialGradient(cx - baseR * 0.25, cy - baseR * 0.25, 0, cx - baseR * 0.25, cy - baseR * 0.25, baseR * 0.5);
    hlGrad.addColorStop(0, 'rgba(255,255,255,.2)');
    hlGrad.addColorStop(0.4, 'rgba(255,255,255,.05)');
    hlGrad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx!.fillStyle = hlGrad; ctx!.fill();
    ctx!.restore();

    // Neon edge ring
    ctx!.save(); ctx!.beginPath();
    for (let i = 0; i <= steps; i++) {
      const angle = (i / steps) * Math.PI * 2;
      const r = blobRadius(angle) + 1.5;
      const x = cx + Math.cos(angle) * r, y = cy + Math.sin(angle) * r;
      if (i === 0) ctx!.moveTo(x, y); else ctx!.lineTo(x, y);
    }
    ctx!.closePath();
    const edgeOpacity = 0.15 + Math.sin(t * 0.03) * 0.08;
    ctx!.strokeStyle = `rgba(${PINK_RGB_NAV},${edgeOpacity})`;
    ctx!.lineWidth = 2; ctx!.shadowColor = PINK_NAV; ctx!.shadowBlur = 8; ctx!.stroke();
    ctx!.restore();

    // Eyes
    const eyeSpacing = baseR * 0.28, eyeY = cy - baseR * 0.05;
    const eyeW = baseR * 0.13, eyeH = baseR * 0.18;
    const blinkCycle = (t % 240) / 240; let eyeScale = 1;
    if (blinkCycle > 0.48 && blinkCycle < 0.52) {
      const bt = (blinkCycle - 0.48) / 0.04;
      eyeScale = bt < 0.5 ? 1 - bt * 2 : bt * 2 - 1;
      eyeScale = Math.max(0.05, eyeScale);
    }
    const lookX = Math.sin(t * 0.012) * eyeW * 0.2;
    const lookY = Math.cos(t * 0.015) * eyeH * 0.15;
    for (let e = -1; e <= 1; e += 2) {
      const ex = cx + e * eyeSpacing;
      ctx!.save(); ctx!.translate(ex, eyeY); ctx!.scale(1, eyeScale);
      ctx!.beginPath(); ctx!.ellipse(0, 0, eyeW, eyeH, 0, 0, Math.PI * 2); ctx!.fillStyle = 'rgba(255,255,255,.95)'; ctx!.fill();
      const pupilR = eyeW * 0.55;
      ctx!.beginPath(); ctx!.arc(lookX, lookY + eyeH * 0.08, pupilR, 0, Math.PI * 2); ctx!.fillStyle = '#1a1a1a'; ctx!.fill();
      ctx!.beginPath(); ctx!.arc(lookX - pupilR * 0.3, lookY - pupilR * 0.2, pupilR * 0.3, 0, Math.PI * 2); ctx!.fillStyle = 'rgba(255,255,255,.7)'; ctx!.fill();
      ctx!.restore();
    }
    _nevaNavRAF = requestAnimationFrame(draw);
  }
  draw();
}
(window as any).initNevaNavCanvas = initNevaNavCanvas;

exposeGlobals();

// DOMContentLoaded — NUR Shell-Listener + Theme initialisieren
// Login/Session-Restoration bleibt bei form.js (vermeidet Konflikte)
function initShellListeners(): void {
  // Auto-Refresh: DEAKTIVIERT in shell.ts — form.js hat eigenen Handler
  // navigateTo('home') loeste das alte TS-Dashboard aus (Race Condition)

  // Scroll-Detection für Header
  window.addEventListener('scroll', () => {
    const header = document.getElementById('dash-header');
    if (header) {
      if (window.scrollY > 20) header.classList.add('scrolled');
      else header.classList.remove('scrolled');
    }
  }, { passive: true });

  // Browser-Back intercepten
  history.replaceState({ nevanu: true }, '', '');
  history.pushState({ nevanu: true }, '', '');
  window.addEventListener('popstate', () => {
    history.pushState({ nevanu: true }, '', '');

    // Form sichtbar? → zurück zum Dashboard
    const app = document.getElementById('app');
    if (app && app.style.display !== 'none') {
      if (typeof (window as any).goBackToDashboard === 'function') (window as any).goBackToDashboard();
      return;
    }

    // Offene Overlays schliessen
    const overlays = [
      'neukunde-detail-overlay', 'protokoll-readonly-overlay',
      'quick-rechner-overlay', 'lager-detail-overlay', 'task-detail-overlay',
    ];
    for (const id of overlays) {
      const ov = document.getElementById(id);
      if (ov) { ov.remove(); return; }
    }

    // Sheets schliessen
    if (document.getElementById('more-sheet-overlay')) { closeMoreSheet(); return; }
    if (document.getElementById('profil-sheet-overlay')) { closeProfilSheet(); return; }

    // Zur Home navigieren
    if (activeDashTab !== 'home') {
      bottomNavGo('home');
    }
  });

  // Theme initialisieren
  initTheme();

  // Dock Magnification (nur Desktop)
  setTimeout(() => initDockMagnification(), 500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShellListeners);
} else {
  initShellListeners();
}

// Re-export
export {
  showDashboard,
  loadDashboard,
  bottomNavGo,
  menuNavigate,
  logoutPin,
  openMoreSheet,
  closeMoreSheet,
  openProfilSheet,
  closeProfilSheet,
};
